import Component from "@glimmer/component"
import { isNil, map, has, omitBy, clone } from "lodash-es"

interface Args {
  src: string
  imgixParams?: ImgixParams
  loading?: string
  height?: string
  width?: string
}

/**
 * Usage:

  <DS::Imgix
    @src="https://commoncurriculum.imgix.net/items/p9uddkq5/download"
    @imgixParams={{hash crop="faces" width="1200"}}
    class="feature-callout__svg"
  />

  or

  <DS::Imgix
    @src="https://commoncurriculum.imgix.net/items/p9uddkq5/download"
    @imgixParams={{hash crop="faces" width="1200"}}
    class="feature-callout__svg"
    @height="100"
    @width="100"
  />


  The imgixParams overwrite all other settings (e.g. width, height) so you
  can set the width/height of the image and send a different setting to imgix
 */
export default class DSImgixComponent extends Component<Args> {
  get srcSet() {
    if (isNil(this.args.src)) return ""
    return map(DPR_QUALITY_VALUES, (q, index) => {
      let dpr = index + 1
      let url = buildURL(
        this.args.src,
        this.buildImgixUrlParams({ imgixParams: this.args.imgixParams, q, dpr })
      )
      return `${url} ${dpr}x`
    }).join(", ")
  }

  /**
   * Will construct params based on the following guidelines:
   * - Will use @height unless the imgixParams include height
   * - Will use @width unless the imgixParams include width.
   * - Will use @q unless igmixParmas include q
   * - Will use @dpr unless imgixParams include DPR
   *
   */
  buildImgixUrlParams({ imgixParams, q, dpr }: Record<string, any>) {
    let params: Record<string, any> = {}

    // As we might want to pass in null, we're not checking if the value is nil
    // instead, we're checking if we're passing in the key. If not,  and we've
    // set an height/width on the image itself, we send that height/width to imigx.
    // The use case here is we're rendering an image in a thumbnail and we set
    // <img heigh=50 width=50> and we want imgix to return an image of the same size.
    // We don't want to have to also do @imigxParams={{hash h=50 w=50}}
    if (!has(imgixParams, "w") && !has(imgixParams, "width") && !isNil(this.args.width)) {
      params.w = this.args.width
    }
    if (!has(imgixParams, "h") && !has(imgixParams, "height") && !isNil(this.args.height)) {
      params.h = this.args.height
    }

    if (q) {
      params.q = q
    }
    if (dpr) {
      params.dpr = dpr
    }

    // Because we'll never remember to pass h/w to `imgixParams` instead of height/width,
    // here we "rename" the param to h or w.
    // Why rename? The src url coming from prismic has height/width set as h/w so
    // we use the same key so they'll properly clobber each other.
    let iParams = clone(imgixParams)
    if (has(iParams, "width")) {
      iParams.w = iParams.width
      delete iParams.width
    }
    if (has(iParams, "height")) {
      iParams.h = iParams.height
      delete iParams.height
    }

    return Object.assign({ auto: "format" }, params, iParams)
  }

  get _src() {
    if (isNil(this.args.src)) return ""
    return buildURL(this.args.src, this.buildImgixUrlParams({ imgixParams: this.args.imgixParams }))
  }

  // Default to "auto"
  get _loading() {
    return this.args.loading || "auto"
  }
}

function extractQueryParams(src) {
  const splitSrc = src.split("?")
  const url = splitSrc[0]
  const query = splitSrc[1]
  if (!query) {
    return [url, {}]
  }
  const paramsPairs = query.split("&")
  const params = {}
  const len = paramsPairs.length
  for (let i = 0; i < len; i++) {
    const param = paramsPairs[i]
    const splitParam = param.split("=")
    const key = splitParam[0]
    const val = splitParam[1]
    params[key] = decodeURIComponent(val)
  }
  return [url, params]
}

const DPR_QUALITY = {
  q_dpr1: 75,
  q_dpr2: 50,
  q_dpr3: 35,
  q_dpr4: 23,
  q_dpr5: 20,
}

const DPR_QUALITY_VALUES = Object.values(DPR_QUALITY)

// Types from https://github.com/DefinitelyTyped/DefinitelyTyped/pull/40289/files#diff-881ddc76d18998f7d8b00df0d8371f8cR201
// TO UPDATE THESE
// - Just copy and paste
// - I only made changes to the LongForm interface to
// incorporate all the long form of the abbreviations they have in the
// PARAMS_EXPANSION object

type ImgixParamType = string | number | boolean

interface AdjustmentParams {
  bri?: ImgixParamType
  con?: ImgixParamType
  exp?: ImgixParamType
  gam?: ImgixParamType
  high?: ImgixParamType
  hue?: ImgixParamType
  invert?: ImgixParamType
  sat?: ImgixParamType
  shad?: ImgixParamType
  sharp?: ImgixParamType
  usm?: ImgixParamType
  usmrad?: ImgixParamType
  vib?: ImgixParamType
}

interface AutomaticParams {
  auto?: ImgixParamType
}

interface BlendingParams {
  "blend-align"?: ImgixParamType
  "blend-alpha"?: ImgixParamType
  "blend-crop"?: ImgixParamType
  "blend-fit"?: ImgixParamType
  "blend-mode"?: ImgixParamType
  "blend-pad"?: ImgixParamType
  "blend-size"?: ImgixParamType
  "blend-x"?: ImgixParamType
  "blend-y"?: ImgixParamType
  blend?: ImgixParamType
}

interface BorderAndPaddingParams {
  "border-radius-inner"?: ImgixParamType
  "border-radius"?: ImgixParamType
  border?: ImgixParamType
  pad?: ImgixParamType
}

interface ColorPaletteParams {
  colors?: ImgixParamType
  palette?: ImgixParamType
  prefix?: ImgixParamType
}

interface FaceDetectionParams {
  faceindex?: ImgixParamType
  facepad?: ImgixParamType
  faces?: ImgixParamType
}

interface FillParams {
  bg?: ImgixParamType
  "fill-color"?: ImgixParamType
  fill?: ImgixParamType
}

interface FocalPointCropParams {
  "fp-debug"?: ImgixParamType
  "fp-x"?: ImgixParamType
  "fp-y"?: ImgixParamType
  "fp-z"?: ImgixParamType
}

interface FormatParams {
  ch?: ImgixParamType
  chromasub?: ImgixParamType
  colorquant?: ImgixParamType
  cs?: ImgixParamType
  dl?: ImgixParamType
  dpi?: ImgixParamType
  fm?: ImgixParamType
  lossless?: ImgixParamType
  q?: ImgixParamType
}

interface MaskImageParams {
  "corner-radius"?: ImgixParamType
  mask?: ImgixParamType
}

interface NoiseReductionParams {
  nr?: ImgixParamType
  nrs?: ImgixParamType
}

interface PDFParams {
  page?: ImgixParamType
}

interface PixelDensityParams {
  dpr?: ImgixParamType
}

interface RotationParams {
  flip?: ImgixParamType
  orient?: ImgixParamType
  rot?: ImgixParamType
}

interface SizeParams {
  ar?: ImgixParamType
  crop?: ImgixParamType
  fit?: "clamp" | "clip" | "crop" | "facearea" | "fill" | "fillmax" | "max" | "min" | "scale"
  h?: ImgixParamType
  "max-h"?: ImgixParamType
  "max-w"?: ImgixParamType
  "min-h"?: ImgixParamType
  "min-w"?: ImgixParamType
  rect?: ImgixParamType
  w?: ImgixParamType
}

interface StylizeParams {
  blur?: ImgixParamType
  "duotone-alpha"?: ImgixParamType
  duotone?: ImgixParamType
  htn?: ImgixParamType
  monochrome?: ImgixParamType
  px?: ImgixParamType
  sepia?: ImgixParamType
}

interface TextParams {
  "txt-align"?: ImgixParamType
  "txt-clip"?: ImgixParamType
  "txt-color"?: ImgixParamType
  "txt-fit"?: ImgixParamType
  "txt-font"?: ImgixParamType
  "txt-lig"?: ImgixParamType
  "txt-line-color"?: ImgixParamType
  "txt-line"?: ImgixParamType
  "txt-pad"?: ImgixParamType
  "txt-shad"?: ImgixParamType
  "txt-size"?: ImgixParamType
  "txt-width"?: ImgixParamType
  txt?: ImgixParamType
}

interface TrimParams {
  "trim-color"?: ImgixParamType
  "trim-md"?: ImgixParamType
  "trim-sd"?: ImgixParamType
  "trim-tol"?: ImgixParamType
  trim?: ImgixParamType
}

interface TypesettingEndpointParams {
  "txt-lead"?: ImgixParamType
  "txt-track"?: ImgixParamType
  "~text"?: ImgixParamType
}

interface WatermarkParams {
  "mark-align"?: ImgixParamType
  "mark-alpha"?: ImgixParamType
  "mark-base"?: ImgixParamType
  "mark-fit"?: ImgixParamType
  "mark-h"?: ImgixParamType
  "mark-pad"?: ImgixParamType
  "mark-scale"?: ImgixParamType
  "mark-w"?: ImgixParamType
  "mark-x"?: ImgixParamType
  "mark-y"?: ImgixParamType
  mark?: ImgixParamType
}

interface LongFormParams {
  vibrance?: ImgixParamType
  "unsharp-radius"?: ImgixParamType
  "unsharp-mask"?: ImgixParamType
  sharpness?: ImgixParamType
  shaddows?: ImgixParamType
  saturation?: ImgixParamType
  highlights?: ImgixParamType
  gamma?: ImgixParamType
  exposure?: ImgixParamType
  contrast?: ImgixParamType
  brightness?: ImgixParamType
  "auto-features"?: ImgixParamType
  "background-color"?: ImgixParamType
  padding?: ImgixParamType
  "face-index"?: ImgixParamType
  "face-padding"?: ImgixParamType
  download?: ImgixParamType
  DPI?: ImgixParamType
  format?: ImgixParamType
  quality?: ImgixParamType
  "noise-blur"?: ImgixParamType
  "flip-direction"?: ImgixParamType
  orientation?: ImgixParamType
  "rotation-angle"?: ImgixParamType
  "crop-mode"?: ImgixParamType
  "fit-mode"?: ImgixParamType
  "image-height"?: ImgixParamType
  "image-width"?: ImgixParamType
  blurring?: ImgixParamType
  halftone?: ImgixParamType
  monotone?: ImgixParamType
  pixelate?: ImgixParamType
  "sepia-tone"?: ImgixParamType
  height?: ImgixParamType
  width?: ImgixParamType
}

type ImgixParams = LongFormParams &
  AdjustmentParams &
  AutomaticParams &
  BlendingParams &
  BorderAndPaddingParams &
  ColorPaletteParams &
  FaceDetectionParams &
  FillParams &
  FocalPointCropParams &
  FormatParams &
  MaskImageParams &
  NoiseReductionParams &
  PDFParams &
  PixelDensityParams &
  RotationParams &
  SizeParams &
  StylizeParams &
  TextParams &
  TrimParams &
  TypesettingEndpointParams &
  WatermarkParams

/*
Copyright © 2015 by Coursera
Licensed under the Apache License 2.0, seen https://github.com/coursera/react-imgix/blob/master/LICENSE
Minor syntax modifications have been made
*/

// var Base64 = require("js-base64").Base64

// @see https://www.imgix.com/docs/reference
var PARAM_EXPANSION = Object.freeze({
  // Adjustment
  brightness: "bri",
  contrast: "con",
  exposure: "exp",
  gamma: "gam",
  highlights: "high",
  hue: "hue",
  invert: "invert",
  saturation: "sat",
  shaddows: "shad",
  sharpness: "sharp",
  "unsharp-mask": "usm",
  "unsharp-radius": "usmrad",
  vibrance: "vib",

  // Automatic
  "auto-features": "auto",

  // Background
  "background-color": "bg",

  // Blend
  blend: "blend",
  "blend-mode": "bm",
  "blend-align": "ba",
  "blend-alpha": "balph",
  "blend-padding": "bp",
  "blend-width": "bw",
  "blend-height": "bh",
  "blend-fit": "bf",
  "blend-crop": "bc",
  "blend-size": "bs",
  "blend-x": "bx",
  "blend-y": "by",

  // Border & Padding
  border: "border",
  padding: "pad",

  // Face Detection
  "face-index": "faceindex",
  "face-padding": "facepad",
  faces: "faces",

  // Format
  "chroma-subsampling": "chromasub",
  "color-quantization": "colorquant",
  download: "dl",
  DPI: "dpi",
  format: "fm",
  "lossless-compression": "lossless",
  quality: "q",
  dpr: "dpr",

  // Mask
  "mask-image": "mask",

  // Mask
  "noise-blur": "nr",
  "noise-sharpen": "nrs",

  // Palette n/a
  // PDF n/a
  // Pixel Density n/a

  // Rotation
  "flip-direction": "flip",
  orientation: "or",
  "rotation-angle": "rot",

  // Size
  "crop-mode": "crop",
  "fit-mode": "fit",
  "image-height": "h",
  "image-width": "w",

  // Stylize
  blurring: "blur",
  halftone: "htn",
  monotone: "mono",
  pixelate: "px",
  "sepia-tone": "sepia",

  // Trim TODO
  // Watermark TODO

  // Extra
  height: "h",
  width: "w",
})

var DEFAULT_OPTIONS = Object.freeze({
  auto: "format", // http://www.imgix.com/docs/reference/automatic#param-auto
})

/**
 * Construct a URL for an image with an Imgix proxy, expanding image options
 * per the [API reference docs](https://www.imgix.com/docs/reference).
 * @param  {String} src         src of raw image
 * @param  {Object} longOptions map of image API options, in long or short form per expansion rules
 * @return {String}             URL of image src transformed by Imgix
 */
function constructUrl(src, longOptions) {
  if (!src) {
    return ""
  }

  const keys = Object.keys(longOptions)
  const keysLength = keys.length
  let url = src + "?"
  for (let i = 0; i < keysLength; i++) {
    let key = keys[i]
    let val = longOptions[key]

    if (PARAM_EXPANSION[key]) {
      key = PARAM_EXPANSION[key]
    } else {
      key = encodeURIComponent(key)
    }

    // if (key.substr(-2) === "64") {
    //   val = Base64.encodeURI(val)
    // }

    url += key + "=" + encodeURIComponent(val) + "&"
  }

  return url.slice(0, -1)
}

function buildURL(src, imgixParams = {}) {
  const [rawSrc, params] = extractQueryParams(src)
  let urlParams = omitBy(Object.assign({}, params, imgixParams), (v, k) => isNil(v))
  return constructUrl(rawSrc, urlParams)
}
